import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { ChevronDown } from "lucide-react";
import {
  deleteConversationFirestore,
  updateConversationNameFirestore,
} from "../data/firestore";
import { hasRealTargetName } from "../data/helpers";
import { useUser } from "../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { useEffect, useState } from "react";

interface ConversationDropdownMenuProps {
  conversationName: string;
  targetName: string;
  conversationId: string;
}

export const ConversationDropdownMenu = ({
  conversationName,
  targetName,
  conversationId,
}: ConversationDropdownMenuProps) => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const currentName = hasRealTargetName(targetName) ? targetName : "";
  const [newName, setNewName] = useState(currentName);

  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Reset input value when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setNewName(currentName);
    }
  }, [currentName, isOpen]);

  const handleDelete = async () => {
    if (!userData) return;

    try {
      await deleteConversationFirestore(userData, conversationId);
      navigate("/");
    } catch (error) {
      console.error("Failed to delete conversation:", error);
      // Optionally add error handling (toast notification, etc.)
    }
  };

  const handleRename = async () => {
    if (!userData) return;

    try {
      await updateConversationNameFirestore(
        userData.uid,
        conversationId,
        newName,
      );
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to rename conversation:", error);
      // Optionally add error handling (toast notification, etc.)
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="text-foreground py-0 hover:bg-gray-200 focus-visible:ring-0 max-w-[80%]"
          >
            <h2 className="text-lg sm:text-xl font-bold text-center truncate max-w-[90%]">
              {conversationName}
            </h2>
            <ChevronDown className="ml-1 mt-1 flex-shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="font-medium"
            onSelect={() => setIsOpen(true)}
          >
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            className="font-medium text-red-600"
            onSelect={() => setIsDeleteOpen(true)}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              conversation “{conversationName}”.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-500"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="sm:max-w-[425px]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Rename conversation</DialogTitle>
            <DialogDescription>
              Enter the name of the person you are speaking to
            </DialogDescription>
          </DialogHeader>
          <div className="">
            <div className="items-center gap-4">
              {/* <Label htmlFor="name">Name</Label> */}
              <Input
                id="name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={handleRename}
              disabled={!newName.trim() || newName.trim() === currentName}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
